import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { confirm, haptic } from '../lib/telegram';
import AmountInput from '../components/AmountInput';
import { formatAmount, parseAmountInput } from '../lib/format';
import type { Debt, Currency } from '../lib/types';

interface Props { onBack: () => void; }

export default function Debts({ onBack }: Props) {
  const [debts, setDebts] = useState<Debt[]>([]);
  const [direction, setDirection] = useState<'owed_to_me' | 'i_owe'>('owed_to_me');
  const [counterparty, setCounterparty] = useState('');
  const [currency, setCurrency] = useState<Currency>('SYP');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [cashChanged, setCashChanged] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setErr(null);
    try {
      const r = await api.listDebts();
      setDebts(r.debts);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const amt = parseAmountInput(amount);
    if (!counterparty.trim()) { setErr('Enter a name'); return; }
    if (amt === null) { setErr('Amount must be positive'); return; }
    setErr(null); setBusy(true);
    const name = counterparty.trim();
    try {
      await api.addDebt({ direction, counterparty: name, currency, amount: amt, note: note.trim() || null });
      if (cashChanged) {
        // Lending out → cash leaves you (expense); borrowing → cash comes in (income).
        // category 'Loan' is balance-only, so this affects balance but stays out of spending analytics.
        const kind = direction === 'owed_to_me' ? 'expense' : 'income';
        const verb = direction === 'owed_to_me' ? 'Lent to' : 'Borrowed from';
        await api.create({
          kind, currency, amount: amt,
          category: 'Loan',
          note: `${verb} ${name}`,
        });
      }
      setCounterparty(''); setAmount(''); setNote('');
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed');
    } finally { setBusy(false); }
  }

  async function settle(d: Debt) {
    // For OPEN debts: prompt whether cash actually changed hands.
    // For SETTLED debts (toggle back to open): just reopen — don't ask, no entry made.
    try {
      if (d.status === 'open') {
        const withCash = await confirm('Did cash change hands? OK logs the cash movement; Cancel just marks settled.');
        await api.updateDebt(d.id, { status: 'settled' });
        if (withCash) {
          // Repaid owed_to_me → cash comes in (income). Repaid i_owe → cash leaves (expense).
          const kind = d.direction === 'owed_to_me' ? 'income' : 'expense';
          const verb = d.direction === 'owed_to_me' ? 'Repaid by' : 'Repaid to';
          await api.create({
            kind,
            currency: d.currency,
            amount: Number(d.amount),
            category: 'Loan',
            note: `${verb} ${d.counterparty}`,
          });
        }
      } else {
        await api.updateDebt(d.id, { status: 'open' });
      }
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed');
    }
  }
  async function remove(d: Debt) {
    if (!(await confirm(`Delete this record for ${d.counterparty}?`))) return;
    try { await api.removeDebt(d.id); haptic('success'); await load(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'failed'); }
  }

  const open = debts.filter((d) => d.status === 'open');
  const owedToMe = open.filter((d) => d.direction === 'owed_to_me');
  const iOwe     = open.filter((d) => d.direction === 'i_owe');

  function totals(list: Debt[]): string {
    const t: Record<Currency, number> = { USD: 0, SYP: 0 };
    for (const d of list) t[d.currency] += Number(d.amount);
    const parts: string[] = [];
    if (t.SYP) parts.push(formatAmount(t.SYP, 'SYP'));
    if (t.USD) parts.push(formatAmount(t.USD, 'USD'));
    return parts.join(' · ') || '—';
  }

  function section(label: string, list: Debt[], cls: string) {
    return (
      <div className="card">
        <div className="section-title">
          <span>{label}</span>
          <span className={`report-section-right ${cls}`}>{totals(list)}</span>
        </div>
        {list.length === 0 ? (
          <div className="empty" style={{ padding: '14px 0' }}>Nothing here.</div>
        ) : list.map((d) => (
          <div className="row" key={d.id}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>{d.counterparty}</div>
              {d.note && <div className="muted small ellipsis">{d.note}</div>}
            </div>
            <div className={`${cls}`} style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              {formatAmount(Number(d.amount), d.currency)}
            </div>
            <button onClick={() => settle(d)} style={{ background: 'transparent', border: 'none', color: 'var(--link)', cursor: 'pointer' }}>
              Settle
            </button>
            <button onClick={() => remove(d)} style={{ background: 'transparent', border: 'none', color: 'var(--destructive)', cursor: 'pointer' }}>✕</button>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>Debts</h1>
      </div>
      {err && <div className="error">{err}</div>}

      <form className="card" onSubmit={add}>
        <div className="section-title">Add a record</div>
        <div className="segmented">
          <button type="button" className={direction === 'owed_to_me' ? 'active' : ''} onClick={() => setDirection('owed_to_me')}>Owed to me</button>
          <button type="button" className={direction === 'i_owe' ? 'active' : ''} onClick={() => setDirection('i_owe')}>I owe</button>
        </div>
        <label className="label">Person</label>
        <input className="input" value={counterparty} onChange={(e) => setCounterparty(e.target.value)} placeholder="e.g. Sami" maxLength={60} />
        <label className="label">Currency</label>
        <div className="segmented">
          <button type="button" className={currency === 'SYP' ? 'active' : ''} onClick={() => setCurrency('SYP')}>SYP</button>
          <button type="button" className={currency === 'USD' ? 'active' : ''} onClick={() => setCurrency('USD')}>USD</button>
        </div>
        <label className="label">Amount</label>
        <AmountInput value={amount} onChange={setAmount} currency={currency} />
        <label className="label">Note (optional)</label>
        <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. lunch money" />

        <label className="row" style={{ marginTop: 12, padding: '8px 0', borderTop: 'none', cursor: 'pointer' }}>
          <span>
            💸 Cash changed hands now
            <div className="muted small">
              {cashChanged
                ? `${direction === 'owed_to_me' ? 'Lending out' : 'Borrowing'} — your balance will ${direction === 'owed_to_me' ? 'drop' : 'rise'} to match.`
                : 'Pure ledger record — your balance is unchanged.'}
            </div>
          </span>
          <input
            type="checkbox"
            checked={cashChanged}
            onChange={(e) => setCashChanged(e.target.checked)}
            style={{ width: 22, height: 22, flex: '0 0 22px' }}
          />
        </label>

        <button type="submit" className="btn" style={{ marginTop: 4 }} disabled={busy}>
          {busy ? 'Saving…' : 'Add'}
        </button>
      </form>

      {loading ? (
        <div className="card"><div className="muted">Loading…</div></div>
      ) : <>
        {section('💰 Owed to me', owedToMe, 'amount-pos')}
        {section('💸 I owe', iOwe, 'amount-neg')}
      </>}

      <p className="muted small" style={{ textAlign: 'center' }}>
        Leave "Cash changed hands" ticked when adding so your balance follows the loan.
        Untick for a pure ledger record. Loan entries don't pollute your spending reports.
      </p>
    </>
  );
}
